import { createAsyncThunk } from "@reduxjs/toolkit";
import { captureException } from "@sentry/browser";

import * as wishlistsAPI from "../../../api/wishlists";
import { IWishListLine } from "../../../models/catalogue";
import { LIST_FAVORITES } from "../constants";

/**
 * Load favorites wishlist.
 *
 * Fetches the user's favorites wishlist items. Failures are caught gracefully
 * and logged to Sentry, returning an empty array to prevent checkout disruption.
 *
 * @returns {Promise<IWishListLine[]>} Promise resolving to favorites wishlist lines
 *
 * @example
 * // Load favorites on page mount
 * dispatch(loadFavorites());
 *
 * @example
 * // Reload favorites after adding/removing an item
 * await dispatch(loadFavorites()).unwrap();
 *
 * @sideEffects
 * - Updates `data.wishlists[LIST_FAVORITES]` with fetched wishlist items via extraReducers
 * - Logs errors to Sentry if the API call fails
 */
export const loadFavorites = createAsyncThunk(
    "checkout/loadFavorites",
    async (): Promise<readonly IWishListLine[]> => {
        const favoritesList = await wishlistsAPI
            .listLines(LIST_FAVORITES)
            .catch((err) => {
                captureException(err);
                return [];
            });

        return favoritesList;
    },
);
